import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog } from '@progress/kendo-react-dialogs';
import React from 'react';
import { NavLink, useMatch } from 'react-router';
import { FileSystemExtendedAvailable } from '../../common/helpers';
import { Menu, MenuItem } from '../../components/common/menu/DropdownMenu';
import DiversionManager from '../../components/diversion/diversion-manager';
import DrawingObjectTools from '../../components/drawing-object/DrawingObjectTools';
import { ContextMenus } from '../../components/map-interaction/context-menu/ContextMenus';
import NotificationsList from '../../components/replay/NotificationsList';
import { WeatherDetailsDialog } from '../../components/weather/interaction/WeatherDetailsDialog';
import LayerDebugger from '../../components/weather/LayerDebugger';
import { RecordingStartFailText } from '../../components/weather/settings/settings-options';
import useDebugFeatures from '../../hooks/use-debug-features';
import {
    CodeOutline,
    CogOutline,
    FlightOutline,
    FlightScapeOutline,
    LayersOutline,
    ModeDarkSolid,
    ModeLightSolid,
    TornadoOutline
} from '../../icons/icons';
import { Tutorial } from '../../onboarding/Tutorial';
import { RecordingStartFailErrors } from '../../services/actor-messages';
import { AiChatToolbarButton, AiChatWindow } from '../components/ai-chat/AiChat';
import CommandCenter from '../components/command-center/CommandCenter';
import { DevSettings } from '../components/DevSettings';
import { SendFeedbackWindow } from '../components/feedback/SendFeedbackWindow';
import { FlightDebugger } from '../components/FlightDebugger';
import { FlightObfuscator } from '../components/FlightObfuscator';
import { FullscreenComponent } from '../components/FullscreenComponent';
import { IntegrationMessageDebugger } from '../components/IntegrationMessageDebugger';
import CustomPopover from '../components/layout/CustomPopover';
import { MessageDebugger } from '../components/MessageDebugger';
import { MonitorPopupAlerts } from '../components/monitor/MonitorPopupAlerts';
import ExternalWindows from '../components/multi-window/ExternalWindows';
import { NotamWindow } from '../components/NotamWindow';
import NotesWindow from '../components/NotesWindow';
import { PlaybackDragAndDrop } from '../components/playback/PlaybackDragAndDrop';
import ProfileButton from '../components/ProfileButton';
import RunwayWindsWindow from '../components/RunwayWindsWindow';
import { ScratchpadWindow } from '../components/ScratchpadWindow';
import StatusBar from '../components/status/StatusBar';
import { SystemHubNotifications } from '../components/system-hub/SystemHubNoitification';
import WelcomeHelper from '../components/WelcomeHelper';
import { WorkspacesProvider } from '../contexts/workspace/WorkspacesProvider';
import FlightDataEntryWindow from '../features/flight-data-entry/FlightDataEntryWindow';
import { ExternalWindowsCloseButton } from '../features/home/components/ExternalWindowsCloseButton';
import { ClocksToolbarItem } from '../features/home/components/toolbar/ClocksToolbarItem';
import { ConstraintsToolbarItem } from '../features/home/components/toolbar/ConstraintsToolbarItem';
import DashboardSettingsToolbarItem from '../features/home/components/toolbar/DashboardSettingsToolbarItem';
import { DiversionToolbarItem } from '../features/home/components/toolbar/DiversionToolbarItem';
import { DtnToolbarItem } from '../features/home/components/toolbar/DtnToolbarItem';
import { EliToolbarItem } from '../features/home/components/toolbar/EliToolbarItem';
import { FlightListSwitchToolbarItem } from '../features/home/components/toolbar/FlightListSwitchToolbarItem';
import { MapToolsToolbarItem } from '../features/home/components/toolbar/MapToolsToolbarItem';
import { MonitorStatusToolbarItem } from '../features/home/components/toolbar/MonitorStatusToolbarItem';
import { NetworkToolbarItem } from '../features/home/components/toolbar/NetworkToolbarItem';
import { TutorialToolbarItem } from '../features/home/components/toolbar/TutorialToolbarItem';
import { WorkspacesLayoutToolbarItem } from '../features/home/components/toolbar/WorkspacesLayoutToolbarItem';
import { useAppDispatch, useAppSelector } from '../hooks/store-hooks';
import { useSafeEffect } from '../hooks/use-safe-effect';
import { startRecording } from '../state/reducers/recording';
import { addNotification, NotificationType } from '../state/slices/global-slice';
import { setSetting } from '../state/slices/settings-slice';
import NavMenu from './NavMenu';

// Main frame of the application, include nav menu status bar and all the top level components
export function ApplicationFrame(props: { children?: React.ReactNode }) {
    const [layerDebugger, setLayerDebugger] = React.useState(false);
    const debug = useDebugFeatures();
    const isDark = useAppSelector((x) => x.settings.isDark);
    const [showDevSettings, setShowDevSettings] = React.useState(false);
    const [showWelcomeHelper, setShowWelcomeHelper] = React.useState(false);
    const [showMessageDebugger, setShowMessageDebugger] = React.useState(false);
    const [showIntegrationMessageDebugger, setShowIntegrationMessageDebugger] = React.useState(false);
    const [showFlightDebugger, setShowFlightDebugger] = React.useState(false);
    const [showScrambler, setShowScrambler] = React.useState(false);
    const fullscreen = useAppSelector((x) => x.global.fullscreen);
    const isHome = useMatch('/');
    const isDashboard = useMatch('/dashboard');
    const dtnButton = useAppSelector((x) => x.user.rights.DtnAviation);
    const aiRight = useAppSelector((x) => x.user.rights.ai);
    const offline = useAppSelector((x) => x.global.network.offline);
    const autostartRecording = useAppSelector((x) => x.settings.autoStartRecording);
    const dispatch = useAppDispatch();

    React.useEffect(() => {
        // TODO: move this somewhere with other permissions
        // Request permissions
        Notification.requestPermission().then();
    }, []);

    useSafeEffect(() => {
        if (autostartRecording && FileSystemExtendedAvailable) {
            dispatch(startRecording({ automated: true }))
                .unwrap()
                .then()
                .catch((reason) => {
                    dispatch(
                        addNotification({
                            type: NotificationType.generalError,
                            customMessage: `Failed to automatically start recording. Reason: ${RecordingStartFailText[reason as RecordingStartFailErrors]}`
                        })
                    );
                });
        }
    }, []);

    return (
        <WorkspacesProvider>
            {showDevSettings && (
                <Dialog title="Settings" onClose={() => setShowDevSettings(false)}>
                    <DevSettings />
                </Dialog>
            )}
            <ExternalWindows />
            {debug && (
                <>
                    {layerDebugger && <LayerDebugger onClose={() => setLayerDebugger(false)} />}
                    {<WelcomeHelper showWindow={showWelcomeHelper} onClose={() => setShowWelcomeHelper(false)} />}
                    {showMessageDebugger && <MessageDebugger onClose={() => setShowMessageDebugger(false)} />}
                    {showFlightDebugger && <FlightDebugger onClose={() => setShowFlightDebugger(false)} />}
                    {showIntegrationMessageDebugger && (
                        <IntegrationMessageDebugger onClose={() => setShowIntegrationMessageDebugger(false)} />
                    )}
                    {showScrambler && <FlightObfuscator onClose={() => setShowScrambler(false)} />}
                </>
            )}
            <FullscreenComponent />
            <Tutorial />
            <SystemHubNotifications />
            <SendFeedbackWindow />
            <MonitorPopupAlerts />
            <NotesWindow />
            <FlightDataEntryWindow />
            <NotamWindow />
            <RunwayWindsWindow />
            <DiversionManager />
            <ContextMenus />
            <WeatherDetailsDialog />
            <CommandCenter />
            <AiChatWindow />
            <ScratchpadWindow />
            <PlaybackDragAndDrop />

            <div className="h-100 w-100 d-flex flex-column">
                {!fullscreen && (
                    <div
                        className="border-bottom px-3 d-flex justify-content-between primary-bg-color flex-shrink-0"
                        style={{ height: 42 }}>
                        <NavLink
                            className="d-flex align-items-center gap-2"
                            to="/"
                            style={{ textDecoration: 'none', color: 'var(--kendo-color-app-on-surface' }}>
                            <SvgIcon icon={FlightScapeOutline} />
                            <span style={{ fontWeight: 700, fontSize: '1.2rem', fontFamily: 'Red Hat Display' }}> Flight Explorer</span>
                        </NavLink>

                        <a className="app-title d-flex align-items-center gap-2" href="/"></a>

                        <div className="d-flex gap-2">
                            {isDashboard && (
                                <>
                                    <div className="d-flex align-items-center gap-2">
                                        <DashboardSettingsToolbarItem />
                                    </div>
                                </>
                            )}
                            {isDashboard && <div className="border-right h-100" style={{ marginRight: '-0.5rem' }}></div>}
                            <div className="d-flex align-items-center">
                                <FlightListSwitchToolbarItem />
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                <NetworkToolbarItem />
                                {debug && (
                                    <CustomPopover
                                        trigger={
                                            <Button
                                                fillMode={'outline'}
                                                className="k-rounded-full"
                                                style={{
                                                    width: 27,
                                                    height: 27
                                                }}
                                                svgIcon={FlightScapeOutline}
                                            />
                                        }>
                                        <Menu label="">
                                            <MenuItem
                                                label="Welcome helper"
                                                icon={LayersOutline}
                                                onClick={() => setShowWelcomeHelper(true)}
                                            />
                                            <MenuItem
                                                icon={TornadoOutline}
                                                label="Obfuscate information"
                                                onClick={() => setShowScrambler(true)}
                                            />
                                            <MenuItem
                                                label="Map layers debugger"
                                                icon={LayersOutline}
                                                onClick={() => setLayerDebugger(true)}
                                            />
                                            <MenuItem
                                                label="Settings debugger"
                                                onClick={() => setShowDevSettings(true)}
                                                icon={CogOutline}
                                            />
                                            <MenuItem
                                                label="Message debugger"
                                                icon={CodeOutline}
                                                onClick={() => setShowMessageDebugger(true)}
                                            />
                                            <MenuItem
                                                label="Integration message debugger"
                                                icon={CodeOutline}
                                                onClick={() => setShowIntegrationMessageDebugger(true)}
                                            />
                                            <MenuItem
                                                label="Flight debugger"
                                                icon={FlightOutline}
                                                onClick={() => setShowFlightDebugger(true)}
                                            />
                                        </Menu>
                                    </CustomPopover>
                                )}
                                <DtnToolbarItem />
                            </div>

                            {(dtnButton || debug || offline) && <div className="border-right h-100"></div>}

                            <div className="d-flex align-items-center gap-2">
                                <EliToolbarItem />
                                {isHome && (
                                    <>
                                        <DrawingObjectTools />
                                        <WorkspacesLayoutToolbarItem />
                                        <MapToolsToolbarItem />
                                        <DiversionToolbarItem />
                                        <ConstraintsToolbarItem />
                                        <MonitorStatusToolbarItem />
                                        <ClocksToolbarItem />
                                        <ExternalWindowsCloseButton />
                                    </>
                                )}
                            </div>

                            <div className="border-right"></div>

                            <div className="d-flex gap-2 align-items-center" id="nav-toolbar-icons">
                                <TutorialToolbarItem />
                                <Button
                                    data-tooltip-bottom
                                    title="Change app theme"
                                    svgIcon={isDark ? ModeLightSolid : ModeDarkSolid}
                                    style={{ width: 27, height: 27 }}
                                    className="k-rounded-full"
                                    onClick={() => dispatch(setSetting('isDark', !isDark))}></Button>
                            </div>
                            <div className="border-right"></div>
                            {aiRight && (
                                <div className="d-flex align-items-center">
                                    <AiChatToolbarButton />
                                </div>
                            )}
                            <div className="d-flex align-items-center" id="account-btn">
                                <ProfileButton />
                            </div>
                        </div>
                    </div>
                )}

                <div className="d-flex flex-grow-1 min-h-0">
                    {!fullscreen && (
                        <div className="flex-shrink-0">
                            <NavMenu />
                        </div>
                    )}
                    <div id="app-router-content" className="flex-grow-1 min-w-0 z-1">
                        {props.children}
                    </div>
                </div>

                {!fullscreen && (
                    <div style={{ height: 25 }} className="bg-color border-top flex-shrink-0">
                        <StatusBar />
                    </div>
                )}
            </div>
            <NotificationsList />
        </WorkspacesProvider>
    );
}
